import { forwardRef, useMemo } from 'react'
import { useField } from 'react-final-form'
import { validate as formValidate } from '@captaincause/lib/form/validators'
import { nextTick } from '@captaincause/lib/nextTick'
import { TextInput } from '.'
import type { InputProps } from '.'
import type { ValidatorsAndCustoms } from '@captaincause/lib/form/validators'
import type { Locales } from '@captaincause/types'

interface TextInputFieldProps
  extends Omit<InputProps, 'field' | 'required' | 'value'> {
  name: string
  parse?: (value: string) => string
  onError?: (error?: string) => void
  validators?: ValidatorsAndCustoms
  locale: Locales
  value?: string | number
  ref?: any
  ignoreFormMetaError?: boolean
}

function getFieldType(validators: ValidatorsAndCustoms) {
  if (validators.mustBeEmailAddress) {
    return 'email'
  }

  if (validators.mustBeNumber || validators.mustBeNumberOrUndefined) {
    return 'number'
  }

  return 'text'
}

function fieldParse(value: string, type: ReturnType<typeof getFieldType>) {
  if (type === 'number') {
    return value ? parseFloat(value) : ''
  }

  return value
}

const emptyObjet = {}
export function TextInputFieldWithRef(
  {
    locale,
    name,
    onError = undefined,
    parse: propsParse = undefined,
    validators = emptyObjet,
    value: propsValue = undefined,
    ignoreFormMetaError = false,
    ...props
  }: TextInputFieldProps,
  ref: any,
) {
  const isFieldValueIntercepted = propsValue !== undefined

  const fieldType = getFieldType(validators)

  // We cast 'value' as string here because even if the input could be a number, the value from parse is always a string (since its coming form DOM)
  const parse = (value: string | number) =>
    propsParse
      ? propsParse(value as string)
      : fieldParse(value as string, fieldType)

  const { input, meta } = useField<
    string | number,
    HTMLInputElement | HTMLTextAreaElement
  >(name, {
    parse,
    validate: isFieldValueIntercepted
      ? undefined
      : formValidate(validators, locale),
  })

  const errorForInterceptedValue = useMemo(
    () =>
      isFieldValueIntercepted
        ? formValidate(validators, locale)(propsValue)
        : undefined,
    [isFieldValueIntercepted, locale, validators, propsValue],
  )

  if (isFieldValueIntercepted) {
    nextTick(() => onError?.(errorForInterceptedValue))
  }

  const enhancedFormMeta = useMemo(
    () => ({
      ...meta,
      error: ignoreFormMetaError
        ? errorForInterceptedValue
        : errorForInterceptedValue ?? meta.error,
    }),
    [meta, errorForInterceptedValue, ignoreFormMetaError],
  )

  return (
    <TextInput
      ref={ref}
      blur={input.onBlur}
      change={input.onChange}
      focus={input.onFocus}
      formMeta={enhancedFormMeta}
      name={name}
      required={!!validators.mustBeRequired}
      type={getFieldType(validators)}
      value={propsValue ?? input.value}
      {...props}
    />
  )
}

export const TextInputField = forwardRef(TextInputFieldWithRef)
